import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  DEFAULT_SETTINGS,
  ProgramStoreBuilder,
  channelsWorthAsking,
  buildGuideCategories,
  channelsOfCategory,
  CATEGORY_ALL,
  guideRefreshWindow,
  reminderKey,
  upcomingReminders,
  expiredReminders,
  toggleFavorite,
  windowFromStart,
  type Channel,
  type Credentials,
  type GatewayError,
  type PlayerSettings,
  changeSetting,
  nextSettingValue,
  type PhoneCycleKey,
  type Program,
  type ProgramStore,
} from '@player/domain';
import { FavoritesScreen, LiveScreen, PhoneGuideScreen, PhoneRemindersScreen, PhoneSettingsScreen, PinScreen, RemindersScreen, SettingsScreen, useToast, useTranslator, type MenuEntry, type PhoneSettingChoice } from '@player/ui';
import { AppChrome } from './AppChrome.js';
import { usePhoneLayout } from './usePhoneLayout.js';
import { useLandscape } from './useLandscape.js';
import { useReminderAlerts } from './useReminderAlerts.js';
import { appVersion } from './version.js';
import { checkForUpdate } from './updateCheck.js';
import type { Composition } from './composition.js';
import { GUIDE_REFRESH_MS } from './catalogCache.js';
import type { CatalogCache } from './catalogCache.js';
import { VodHost } from './VodHost.js';
import { SearchHost } from './SearchHost.js';
import { HomeHost } from './HomeHost.js';
import { AggregatorHost } from './AggregatorHost.js';

/**
 * Quel che sta **intorno** alla diretta: i dati della guida e le prop che non devono ballare.
 *
 * ## Perché è un componente e non due righe dentro `App`
 *
 * Perché gli hook non stanno dentro un ramo. `App` sceglie fra accesso, diretta e banco con un
 * ternario: memorizzare lì le prop della diretta vorrebbe dire chiamare `useMemo` solo quando la
 * schermata è quella, che React non permette. Il risultato è che le prop si scrivevano in linea, e
 * ogni disegno ne produceva quattro nuove.
 *
 * ## Cosa costava
 *
 * Il 28-09-2026 l'app è rimasta bloccata su «Avvio del canale…» senza mai avanzare. La prima causa
 * era nella schermata ed è corretta lì; la seconda era qui: `buildStore` ricostruiva **12.083
 * canali a ogni disegno**, e con essi un `GuideController` nuovo, buttando via il precedente.
 * Nessuna delle due si vede leggendo il codice — si vede solo guardando un'app che non avanza.
 *
 * La regola che ne resta: una prop passata a una schermata che tiene stato — un lettore, un
 * controllore, un timer — **non può essere costruita durante il disegno**.
 */

interface LiveHostProps {
  readonly composition: Composition;
  /** Il catalogo sull'apparecchio: categorie ed EPG passano di qui, non dal gateway. */
  readonly cache: CatalogCache;
  readonly channels: ReadonlyArray<Channel>;
  readonly credentials: Credentials;
  readonly guideStartMs: number;
  readonly guideMinutes: number;
  /** Scadenza e connessioni in uso, già in forma leggibile: l'assistenza le chiede per prime. */
  readonly subscription: string;
  readonly onExit: () => void;
  /**
   * La lingua è cambiata nelle impostazioni.
   *
   * La tiene chi monta `I18nProvider`, che sta sopra questa schermata: senza avvisarlo la scelta
   * avrebbe effetto solo al riavvio, e chi l'ha appena fatta concluderebbe che il comando è rotto.
   */
  readonly onLocaleChange: (locale: PlayerSettings['locale']) => void;
  readonly onSendBackToLogin: (error: GatewayError) => void;
  /**
   * Il catalogo è stato riletto: chi lo possiede lo ricarichi.
   *
   * L'elenco dei canali vive in `App`, perché è lui a comporlo all'accesso. Questo componente lo
   * riceve come prop, quindi non può sostituirlo da sé — lo dice, e chi lo tiene se lo riprende dal
   * magazzino, che nel frattempo è già aggiornato.
   */
  readonly onCatalogRefreshed: () => void;
  /** L'ultimo canale guardato su questo apparecchio, già letto dall'archivio. */
  readonly lastChannelId: string | null;
}

/** I programmi arrivati finora, per canale. Cresce; non si richiede mai due volte lo stesso. */
type LoadedPrograms = ReadonlyMap<string, ReadonlyArray<Program>>;


/**
 * Come si chiama, nelle impostazioni, la voce che il telefono chiama in un altro modo.
 *
 * La schermata parla di `language` perché è quello che c'è scritto sulla riga; il dominio tiene
 * `locale`, perché non è solo una lingua — `auto` è «quella del sistema». I due vocabolari non si
 * fanno combaciare rinominando uno dei due: il nome giusto è diverso ai due lati.
 *
 * È un `Record` completo e non una funzione con un `default`: aggiungendo domani una voce che gira,
 * **questo file smette di compilare** finché qualcuno non dice a quale impostazione corrisponde.
 * Con un `default` la voce nuova girerebbe in silenzio sul campo sbagliato.
 */
const CYCLE_KEY: Readonly<Record<PhoneSettingChoice, PhoneCycleKey>> = {
  imageQuality: 'imageQuality',
  guideHours: 'guideHours',
  language: 'locale',
};

export function LiveHost({
  composition,
  cache,
  channels,
  credentials,
  guideStartMs,
  guideMinutes,
  subscription,
  onExit,
  onLocaleChange,
  onSendBackToLogin,
  onCatalogRefreshed,
  lastChannelId,
}: LiveHostProps): ReactNode {
  const [loaded, setLoaded] = useState<LoadedPrograms>(new Map());
  const [favorites, setFavorites] = useState<ReadonlyArray<string>>([]);
  /*
    I promemoria già impostati, **dall'archivio locale**.

    Stavano su `line.app_data`, cioè sul gateway, ed erano l'ultimo rimasto: preferiti, impostazioni,
    «la mia lista» e l'avanzamento erano già scesi in locale. Un promemoria è un avviso su un
    apparecchio — chi lo mette davanti alla TV del salotto vuole che suoni lì, non sul telefono di un
    altro della famiglia sulla stessa linea.
  */
  const [reminders, setReminders] = useState<ReadonlyArray<string>>([]);
  /*
    I nomi delle categorie, per identificativo.

    Una chiamata sola all'ingresso: le categorie cambiano quando cambia il catalogo, non mentre si
    guarda la televisione. Se non arriva, la guida mostra «categoria senza nome» invece degli UUID —
    peggio di avere i nomi, molto meglio di prima, e soprattutto **dichiarato** anziché apparire come
    un dato buffo.
  */
  const [categoryNames, setCategoryNames] = useState<ReadonlyMap<string, string>>(new Map());
  const [settings, setSettings] = useState<PlayerSettings>(DEFAULT_SETTINGS);
  /**
   * Il PIN del controllo genitori, o `null` se non ne è stato impostato uno.
   *
   * Sta qui e non dentro le schermate perché lo leggono in tre: le impostazioni per dire «attivo»,
   * la pagina del PIN per verificarlo, e la richiesta prima di riprodurre. Tenerlo in una schermata
   * vorrebbe dire rileggerlo dall'archivio a ogni apertura.
   */
  const [storedPin, setStoredPin] = useState<string | null>(null);

  /*
    Quale sezione, e se i tasti sono del menu.

    Erano due booleani — `showFavorites` e `showSettings` — e due booleani per tre stati sono uno
    stato in più di quelli veri: `true`/`true` non significa niente, e il codice deve ricordarsi di
    non produrlo. Una sezione sola non può essere in due posti.
  */
  const [section, setSection] = useState<Section>('live');
  const [menuFocused, setMenuFocused] = useState(false);
  /**
   * Schermo intero chiesto **a mano**, col tasto.
   *
   * In orizzontale non serve chiederlo: girare il telefono è già la richiesta. Questo stato vale per
   * il verticale, dove l'immagine divide lo spazio con un'interfaccia disegnata per un televisore —
   * su 390 px è troppo grande, e il tasto è la via d'uscita.
   */
  const [fullscreenAsked, setFullscreenAsked] = useState(false);
  /*
    Quale disegno usare, deciso dalla **larghezza** e rivalutato mentre l'app è aperta.

    Sta qui con gli altri hook e non accanto al ramo che lo usa: dentro un ramo React smetterebbe di
    riconoscere la lista degli hook, e il guasto non si presenta qui ma come stato di un altro
    componente che ricompare nel posto sbagliato.
  */
  const phone = usePhoneLayout();
  /*
    Girato in orizzontale: il telefono chiede lo schermo intero senza dirlo.

    È una domanda diversa da «che forma ha questo apparecchio», che non cambia ruotando — e tenerle
    insieme era il difetto che faceva ripartire il canale a ogni rotazione.
  */
  const landscape = useLandscape();

  /*
    Quale canale è aperto nella guida del telefono.

    Sul televisore la guida è una griglia e il palinsesto sta sempre a schermo; su un telefono non
    ci sta, quindi l'elenco si apre una riga per volta. Lo stato sta qui e non nella schermata
    perché aprire un canale **chiede i suoi programmi**, e chi li possiede è questo componente.
  */
  const [openChannelId, setOpenChannelId] = useState<string | null>(null);
  /*
    Sul telefono si sta guardando: è lo stato che decide fra la guida e il lettore.

    **Non** si riusa `watching`, che è quello che la diretta riferisce: quello diventa `false` anche
    su un errore o durante un'attesa, e la guida ricomparirebbe **sopra** il lettore proprio mentre
    qualcuno sta leggendo il messaggio d'errore. Qui invece lo decide chi tocca: si entra toccando un
    canale, si esce col tasto Indietro.
  */
  const [phonePlaying, setPhonePlaying] = useState(false);
  /** La categoria scelta nella guida del telefono. `CATEGORY_ALL` finché non si tocca niente. */
  const [phoneCategoryId, setPhoneCategoryId] = useState<string>(CATEGORY_ALL);
  /*
    Se il video sta andando: lo riferisce la diretta, e serve **solo** a nascondere la colonna.

    È l'unico momento in cui il menu va via — a schermo pieno ruberebbe 280 px all'immagine. Su un
    errore o un'attesa si vede, ed è la correzione chiesta esplicitamente: «se il canale va in errore
    non è possibile cliccare nulla».
  */
  const [watching, setWatching] = useState(false);
  /*
    Il canale chiesto da un'altra schermata — oggi i preferiti — che vince sull'ultimo guardato.

    La diretta legge `initialChannelId` **una volta**, quando si monta, e rientrando dalla sezione
    preferiti si monta davvero: passare qui l'identificativo è quel che fa partire il canale scelto
    invece di quello di prima. Senza, premere una casella dei preferiti riportava alla diretta sul
    canale sbagliato — cioè un tasto che sembra non aver funzionato.
  */
  const [requestedChannelId, setRequestedChannelId] = useState<string | null>(null);
  /*
    Il titolo chiesto dalla ricerca, con la stessa logica del canale qui sopra.

    Si azzera **entrando in una sezione**, non uscendo: `VodHost` lo legge al montaggio, quindi
    lasciandolo scritto si riaprirebbe la stessa scheda la volta dopo che si entra in Film dal menu —
    un tasto che porta dove si era stati invece che dove dice di portare.
  */
  const [requestedVodId, setRequestedVodId] = useState<string | null>(null);
  /** La categoria chiesta dalla Home: la guida si apre già filtrata su quella. */
  const [requestedCategoryId, setRequestedCategoryId] = useState<string | null>(null);
  /**
   * Con che intenzione si sta entrando nella televisione: per **guardare** o per **scegliere**.
   *
   * Sono due porte sulla stessa schermata. Chi accende l'app, e chi preme il nome di un canale in
   * una qualunque lista, vuole vedere quel canale: l'avvio automatico è quello, ed è desiderato.
   * Chi preme «Guida TV» nel menu, o una piastrella di categoria in Home, sta chiedendo l'opposto —
   * «fammi vedere cosa c'è» — e partire a tutto schermo gli copre proprio quello.
   *
   * Sta qui e non dentro la schermata perché **è chi apre la porta a sapere perché la sta
   * aprendo**: da dentro, i due casi sono indistinguibili, ed è esattamente per questo che finora
   * ricevevano lo stesso trattamento.
   */
  const [arriveBrowsing, setArriveBrowsing] = useState(false);
  /*
    L'aggregatore aperto dalla fascia «Esplora», con etichetta e colore già risolti.

    Si porta dietro il nome perché la sua schermata lo intitola, e il gateway restituisce il
    **contenuto** dell'aggregatore, non come si chiama: quello sta nella vetrina, che la Home ha già.
  */
  const [openAggregator, setOpenAggregator] = useState<
    { readonly id: string; readonly label: string; readonly color: string } | null
  >(null);
  /** Vero mentre si rilegge il catalogo dal tasto della colonna. */
  const [refreshing, setRefreshing] = useState(false);
  const [update, setUpdate] = useState<string | null>(null);

  /*
    «C'è una versione nuova?», chiesto **una volta per avvio**.

    Dipendenze vuote di proposito: non è un dato che si aggiorna mentre si guarda la televisione, e
    con una dipendenza qualsiasi questo effetto girerebbe a ogni resa — cioè una richiesta al
    registro per ogni cambio di canale. È già capitato tre volte in questo progetto, sempre così.

    Non si mostra nessun errore: l'aggiornamento è una comodità, non una condizione per guardare la
    televisione, e `checkForUpdate` risponde `null` a tutto quel che non ha capito.
  */
  useEffect(() => {
    let cancelled = false;
    void checkForUpdate().then((release) => {
      if (!cancelled) setUpdate(release?.version ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const gateway = useMemo(() => composition.createGateway(), [composition]);
  const translator = useTranslator();
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    // Dall'archivio locale: una chiamata al gateway solo se manca o è scaduto per ore d'uso.
    void cache.categories(credentials, false).then((names) => {
      if (!cancelled) setCategoryNames(names);
    });
    void cache.favorites().then((ids) => {
      if (!cancelled) setFavorites(ids);
    });
    void cache.reminders().then((keys) => {
      if (!cancelled) setReminders(keys);
    });
    void cache.settings().then((stored) => {
      if (!cancelled) setSettings(stored);
    });
    void cache.adultPin().then((pin) => {
      if (!cancelled) setStoredPin(pin);
    });
    return () => {
      cancelled = true;
    };
  }, [cache, credentials]);

  /*
    I preferiti stanno **su questo apparecchio**, e basta.

    Passavano dal gateway con una differenza, una risposta da credere e un ripristino se falliva. Per
    questa versione è una complicazione che non serve: la sincronia fra apparecchi è una funzione a
    sé — due televisori che tolgono e mettono lo stesso canale insieme sono un conflitto da
    risolvere, non un dettaglio — e non è in questo giro.

    Togliendo il giro di rete sparisce anche il suo modo di fallire, che era muto: la stella si
    accendeva, il salvataggio non riusciva, e tornava com'era senza una parola.
  */
  const onToggleFavorite = useCallback(
    (channelId: string) => {
      const { optimistic } = toggleFavorite(favorites, channelId);
      setFavorites(optimistic);
      // Scrivere in locale non ha un «dopo» da aspettare: quel che si vede è quel che è salvato.
      void cache.saveFavorites(optimistic);
      /*
        Il riscontro, perché il gesto non ne ha uno suo.

        Si preme OK sul logo di un canale e la stella si accende **in basso a sinistra**, dove non si
        sta guardando: senza una parola si preme due volte, e la seconda disfa la prima.
      */
      toast.show(
        translator.t(optimistic.includes(channelId) ? 'toast.favoriteAdded' : 'toast.favoriteRemoved'),
      );
    },
    [favorites, cache, toast, translator],
  );

  /**
   * Mette o toglie un promemoria, e lo **scrive**.
   *
   * ## Il giro che non si chiudeva
   *
   * `readLineData` restituiva i promemoria, l'anteprima della guida li mostrava, e **nessuno li
   * scriveva mai**: l'unica azione della guida che il disegno prevede e che non esisteva. Per giunta
   * il confronto non poteva riuscire comunque — la griglia costruisce un identificativo sintetico
   * dalla posizione (`canale-slot`), gli archiviati erano quelli dell'EPG, e le due forme non
   * coincidono per costruzione.
   *
   * La chiave è ora `reminderKey`: **canale più istante d'inizio**. Non l'id dell'EPG, che cambia a
   * ogni import e farebbe puntare a un programma diverso domani.
   *
   * ## Si scrive in locale, e non c'è più niente che possa fallire a metà
   *
   * Prima la differenza partiva verso il gateway e lo stato si aggiornava ottimisticamente: fallendo
   * la rete si tornava indietro con un avviso. Un gesto da un tocco che ogni tanto si disfa da solo è
   * peggio di uno che non c'è — e per un promemoria, che è un avviso su **questo** apparecchio, il
   * giro di rete non comprava niente.
   *
   * La scrittura locale può ancora fallire: un archivio pieno, un browser che rifiuta di scrivere. In
   * quel caso si torna indietro e lo si dice, perché la riga non deve promettere un promemoria che
   * non è stato salvato. Ma è l'eccezione, non il caso di tutti i giorni.
   */
  const onToggleReminder = useCallback(
    (channelId: string, startMs: number) => {
      const key = reminderKey(channelId, startMs);
      const had = reminders.includes(key);
      const optimistic = had ? reminders.filter((entry) => entry !== key) : [...reminders, key];
      setReminders(optimistic);

      void cache.saveReminders(optimistic).catch(() => {
        setReminders(reminders);
        // Il ripristino silenzioso era il difetto: il promemoria spariva e nessuno sapeva perché.
        toast.show(translator.t('toast.reminderFailed'));
      });

      toast.show(translator.t(had ? 'toast.reminderRemoved' : 'toast.reminderSet'));
    },
    [reminders, cache, toast, translator],
  );

  /*
    Lo store si ricostruisce **solo** quando arrivano programmi nuovi.

    È immutabile per costruzione — è ciò che gli permette di tenere dodicimila righe in array
    tipizzati invece che in oggetti — quindi «aggiornarlo» significa rifarlo. Raro, perché i dati si
    chiedono solo quando il dito si ferma; a ogni disegno, invece, era una scansione di dodicimila
    canali buttata via subito dopo.
  */
  const programs: ProgramStore = useMemo(() => {
    const builder = new ProgramStoreBuilder(guideStartMs);
    for (const channel of channels) {
      builder.addChannel(
        channel.id,
        (loaded.get(channel.id) ?? []).map((program) => ({
          startMs: program.startMs,
          endMs: program.endMs,
          title: program.title,
          genre: program.genre,
          eventKind: program.eventKind,
        })),
      );
    }
    return builder.build();
  }, [channels, loaded, guideStartMs]);

  /*
    I programmi in un elenco piatto, per la ricerca.

    `loaded` è una mappa per canale, e la ricerca guarda tutto insieme. Si compone **solo quando
    l'archivio cambia**: rifarlo a ogni disegno vorrebbe dire ricopiare migliaia di programmi a ogni
    lettera digitata, cioè proprio nel momento in cui si preme un tasto dopo l'altro.

    Ciclo esplicito e non `flatMap`: quello è di Chromium 69 e Tizen 5 monta il 63 (`tizen5.test.ts`).
  */
  const searchablePrograms = useMemo(() => {
    const flat: Program[] = [];
    for (const list of loaded.values()) for (const program of list) flat.push(program);
    return flat;
  }, [loaded]);

  /*
    Il `Set` si costruisce **quando cambiano i preferiti**, non a ogni disegno.

    Lo usa la griglia per decidere la stella riga per riga: ricostruirlo a ogni disegno sarebbe la
    stessa lezione dello store dei programmi, con dodicimila righe che lo interrogano.
  */
  const favoriteIds = useMemo(() => new Set(favorites), [favorites]);
  const reminderIds = useMemo(() => new Set(reminders), [reminders]);

  /**
   * Schermo intero **adesso**: in orizzontale sempre, in verticale se è stato chiesto.
   *
   * Vale solo mentre si sta guardando un canale sul telefono: a schermo intero spariscono marchio e
   * schede, e toglierli da una pagina di elenchi vorrebbe dire lasciare chi la guarda senza modo di
   * uscirne.
   */
  const phoneFullscreen = phone && phonePlaying && (landscape || fullscreenAsked);

  const guideWindow = useMemo(
    () => windowFromStart(guideStartMs, guideMinutes),
    [guideStartMs, guideMinutes],
  );

  const streamUrlFor = useCallback(
    (channel: Channel) => gateway.liveStreamUrl(credentials, channel.id),
    [gateway, credentials],
  );

  const catchupUrlFor = useCallback(
    (channel: Channel, startMs: number) => gateway.catchupStreamUrl(credentials, channel.id, startMs),
    [gateway, credentials],
  );

  const onNeedPrograms = useCallback(
    (ids: ReadonlyArray<string>) => {
      // Solo quelli che mancano: la guida richiede la stessa finestra a ogni sosta, e ripeterli
      // sarebbe una raffica di chiamate per un dato che si ha già.
      const missing = ids.filter((id) => !loaded.has(id));
      if (missing.length === 0) return;

      /*
        **Dall'archivio locale, e basta.** Scorrere la guida non genera nessuna richiesta.

        Prima si chiedeva al gateway quel che mancava: a ogni sosta dello scorrimento partiva una
        richiesta per i canali nuovi, e su un catalogo vero non finivano mai. Adesso la guida legge
        quel che c'è; a riempire l'archivio pensa il giro di fondo, che parte all'avvio e torna ogni
        ora.
      */
      void cache
        .programs(missing, guideStartMs)
        .then((arrivati) => {
          setLoaded((before) => {
            const after = new Map(before);
            for (const [id, programmi] of arrivati) after.set(id, programmi);
            return after;
          });
        })
        .catch(() => {
          // La guida che non arriva non è un guasto della diretta: il canale si guarda lo stesso, e
          // la griglia resta vuota invece di buttare fuori chi sta guardando.
        });
    },
    [cache, loaded, guideStartMs],
  );

  /**
   * La mezz'ora che l'utente sta guardando, leggibile senza dipenderne.
   *
   * Serve al giro di fondo per rimettere in griglia quel che è arrivato. Se la leggesse dalla prop,
   * quella prop tornerebbe fra le dipendenze dell'effetto — cioè il difetto che il giro è stato
   * appena tolto: un effetto che riparte quando cambia un valore da cui non deve dipendere.
   */
  const guideStartMsRef = useRef(guideStartMs);
  guideStartMsRef.current = guideStartMs;

  /*
    Il giro di fondo della guida: all'avvio, e poi ogni ora.

    Nessuna schermata lo aspetta — la guida disegna quel che trova in archivio — quindi può
    prendersi il tempo che serve: duecento canali per richiesta, con mezzo secondo di respiro in
    mezzo.

    ## Non dipende da dove si sta guardando

    Prima dipendeva: la finestra era `guideStartMs`, cioè la prop che segue la mezz'ora tonda in corso,
    e stava fra le dipendenze dell'effetto. Due conseguenze, e il commento dichiarava il contrario di
    entrambe. Il giro ripartiva **ogni mezz'ora** invece che ogni ora, perché il valore cambiava; e
    `GUIDE_REFRESH_MS` non è mai entrato in vigore, perché l'intervallo veniva azzerato prima di
    arrivare a un'ora. Un'ora dichiarata e mezz'ora in atto, senza che niente protestasse.

    Adesso la finestra si calcola **dentro** il giro, ancorata all'ora tonda: due apparecchi accesi
    nella stessa ora chiedono gli stessi estremi, quindi la stessa voce di cache sul backend. Ancorare
    serve a questo — con l'istante esatto ogni apparecchio avrebbe una chiave sua e la cache non
    prenderebbe mai.

    La larghezza è quella che si vede più un'ora di margine: entro il giro dopo la coda è già coperta,
    e quel che sta oltre resta vuoto — che è la regola, non una mancanza.
  */
  useEffect(() => {
    let cancelled = false;
    const channelIds = channels.map((channel) => channel.id);
    /*
      Si chiede solo a chi un palinsesto ce l'ha davvero.

      Misurato il 30-09-2026 su un catalogo cliente: 200 canali chiesti, 17 programmi tornati su 4
      canali. Il giro faceva 57 richieste l'ora per scaricare quel che sta in due, e non poteva fare
      di meglio — quali canali abbiano una guida lo sa solo il database, e adesso lo dice (`has_epg`).

      Il ripiego per i gateway che non conoscono il campo sta dentro `channelsWorthAsking`, e la
      lettura qui sotto resta su **tutti** i canali: legge dall'archivio locale, non costa niente, e un
      canale può avere programmi archiviati da prima che la mappatura si rompesse.
    */
    const askIds = channelsWorthAsking(channels).map((channel) => channel.id);

    const round = () => {
      if (cancelled) return;
      const span = guideRefreshWindow(Date.now(), guideMinutes);
      void cache
        .refreshGuide(credentials, askIds, span.startMs, span.endMs)
        .then(() => {
          if (cancelled) return;
          // Quel che è arrivato entra nella griglia senza che nessuno debba scorrere di nuovo. Qui
          // `guideStartMsRef` e non `from`: la griglia mostra la mezz'ora che l'utente sta guardando,
          // che non è l'ancora del giro.
          void cache.programs(channelIds, guideStartMsRef.current).then((arrived) => {
            if (!cancelled) setLoaded(new Map(arrived));
          });
        });
    };

    round();
    const timer = setInterval(round, GUIDE_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [cache, credentials, channels, guideMinutes]);

  const serverNowMs = composition.clock.isSynced ? composition.clock.nowMs() : guideStartMs;

  /*
    Il nome del profilo, in fondo alla colonna — e solo quando ce n'è uno vero.

    Con l'accesso a utente e password è l'utente; col gettone **non esiste**: la linea non dichiara né
    profilo né anagrafica, e `LoginResult` non li porta. Il prototipo scrive «Marco», che è dato finto:
    riempire quello spazio con «Utente» farebbe credere che l'app sappia chi sta guardando.
  */
  const profileName = credentials.kind === 'standard' ? credentials.username : null;

  /*
    Il salvataggio delle impostazioni, in un punto solo per tutti e due i disegni.

    ## Nell'archivio locale, **non** sul gateway

    Sono impostazioni di questo apparecchio — il televisore vecchio ha bisogno del risparmio risorse,
    il telefono no — e sul gateway sarebbero dell'abbonamento, cioè condivise fra apparecchi che non
    hanno gli stessi limiti.

    Toglie anche il modo di fallire che c'era: prima ogni cambio era una `PATCH`, e in caso di errore
    un `catch` rimetteva le impostazioni di prima. L'interruttore cambiava e tornava indietro da
    solo, senza una parola — irriconoscibile da «il clic non funziona». Vedi `CACHE_KEYS.settings`.

    ## La lingua la tiene `App`, non la schermata

    `I18nProvider` sta sopra di noi: cambiando la voce senza dirlo a chi lo monta, la lingua
    cambierebbe **al riavvio successivo** — e chi l'ha appena scelta concluderebbe che il comando non
    funziona.
  */
  /*
    La guida del telefono: categorie, canali filtrati, e i programmi delle righe che si vedono.

    ## Perché i programmi non sono solo quelli del canale aperto

    Perché **anche le righe chiuse mostrano cosa c'è adesso**: era la prima stesura, e il risultato
    era un elenco in cui ogni riga diceva «palinsesto non disponibile» — cioè una guida che non
    guida.

    ## E perché non tutti

    Perché su un'installazione vera i canali sono dodicimila, e `openedSchedule` filtra l'array per
    canale **a ogni riga**: passare l'intero palinsesto vorrebbe dire scorrerlo dodicimila volte per
    disegnare una schermata. Si prendono solo le prime righe, che sono quelle che entrano nello
    schermo — chi scorre più giù le vedrà quando le avrà chieste scorrendo.

    Non è un'ottimizzazione prematura: il 28-09 l'app è rimasta bloccata su «Avvio del canale…»
    esattamente per un lavoro proporzionale ai dodicimila canali fatto a ogni disegno.
  */
  const phoneCategories = useMemo(
    () => buildGuideCategories(channels, favoriteIds, categoryNames),
    [channels, favoriteIds, categoryNames],
  );

  const phoneChannels = useMemo(
    () => channelsOfCategory(channels, phoneCategoryId, favoriteIds),
    [channels, phoneCategoryId, favoriteIds],
  );

  /** Quante righe della guida si chiedono in anticipo: quelle che stanno su uno schermo, più margine. */
  const PHONE_GUIDE_ROWS = 40;

  const phoneVisible = useMemo(
    () => phoneChannels.slice(0, PHONE_GUIDE_ROWS).map((channel) => channel.id),
    [phoneChannels],
  );

  /*
    Si chiedono i programmi delle righe visibili.

    Solo quando la guida del telefono è a schermo: farlo sempre leggerebbe il palinsesto anche a chi
    sta guardando un film, per una schermata che non vedrà.
  */
  useEffect(() => {
    if (!phone || phonePlaying) return;
    onNeedPrograms(phoneVisible);
  }, [phone, phonePlaying, phoneVisible, onNeedPrograms]);

  const phonePrograms = useMemo(() => {
    const quali = openChannelId === null ? phoneVisible : [...phoneVisible, openChannelId];
    const fuori: Program[] = [];
    for (const id of new Set(quali)) {
      const suoi = loaded.get(id);
      if (suoi !== undefined) fuori.push(...suoi);
    }
    return fuori;
  }, [phoneVisible, openChannelId, loaded]);

  /**
   * Il nome di un canale, o il suo identificativo se non è in catalogo.
   *
   * Uno solo per tutta la schermata: lo chiedono l'avviso, la pagina dei promemoria e la sua
   * versione da televisore, e tre copie della stessa ricerca sono tre posti dove un giorno il
   * ripiego sarà diverso.
   */
  const channelNameOf = useCallback(
    (channelId: string): string => channels.find((c) => c.id === channelId)?.name ?? channelId,
    [channels],
  );

  /** Vai a guardare quel canale: l'azione di una riga dei promemoria, su entrambi gli apparecchi. */
  const watchChannel = useCallback((channelId: string): void => {
    setRequestedChannelId(channelId);
    setArriveBrowsing(false);
    setSection('live');
  }, []);

  const remindersShown = useMemo(
    () => upcomingReminders(reminderIds, phonePrograms, serverNowMs),
    [reminderIds, phonePrograms, serverNowMs],
  );

  /*
    I promemoria scattano **qui**, dove ci sono programmi, promemoria e nomi dei canali.

    Sta in `LiveHost` e non in `App` perché è l'unico punto che ha tutti e tre: `App` non conosce il
    palinsesto, e passarglielo per un avviso vorrebbe dire far salire di un piano un dato che serve
    più in basso.

    I programmi servono **solo per il titolo**. L'ora d'inizio sta nella chiave del promemoria,
    quindi un promemoria per dopodomani scatta anche se il suo palinsesto non è in memoria — prima
    non scattava, ed era la lacuna che rendeva inutili proprio i promemoria messi dalla home.
  */
  useReminderAlerts(phonePrograms, reminders, channelNameOf, () => serverNowMs);

  /*
    I promemoria finiti si **tolgono**, un giro per volta.

    `expiredReminders` c'era e nessuno la chiamava: l'elenco cresceva per sempre, e dopo un mese ogni
    controllo scorreva promemoria di settimane prima. Si potano solo quelli di cui si sa che sono
    passati — l'ora sta nella chiave — e le chiavi del formato vecchio restano dov'erano.
  */
  const scaduti = useMemo(
    () => expiredReminders(reminderIds, phonePrograms, serverNowMs),
    [reminderIds, phonePrograms, serverNowMs],
  );
  useEffect(() => {
    if (scaduti.length === 0) return;
    const restano = reminders.filter((key) => !scaduti.includes(key));
    setReminders(restano);
    void cache.saveReminders(restano);
  }, [scaduti, reminders, cache]);

  const applySettings = (next: PlayerSettings): void => {
    setSettings(next);
    void cache.saveSettings(next);
    if (next.locale !== settings.locale) onLocaleChange(next.locale);
  };

  return (
    /*
      La cornice la sceglie `AppChrome`: colonna laterale sul televisore, barra di schede sul
      telefono. Il contenuto qui sotto è lo stesso — duplicarlo per le due forme vorrebbe dire due
      alberi che divergono in silenzio, con una schermata che su un apparecchio c'è e sull'altro no.
    */
    <AppChrome
      focusPort={composition.focusPort}
      entries={MENU_ENTRIES}
      section={section}
      onPickSection={(voce) => {
        if (isSection(voce)) setSection(voce);
      }}
      /*
        La colonna si vede sempre, **tranne** mentre qualcosa va a schermo pieno — un canale o un film.
        Quando ha il fuoco si vede comunque: chi l'ha chiamata col tasto Indietro deve trovarla.

        Era `section !== 'live' || !watching`, cioè «nascondila solo nella diretta»: con Film e Serie TV
        la stessa condizione avrebbe lasciato 280 px di menu sopra il film. Chi riferisce di stare
        guardando è la schermata, e non c'è motivo perché la regola dipenda da quale sia.
      */
      /*
        A schermo intero il guscio si ritira. Non si sposta niente nell'albero: spostare la schermata
        della diretta sarebbe un rimontaggio, cioè il canale che riparte — il difetto appena corretto.
      */
      hideChrome={phoneFullscreen}
      menuVisible={menuFocused || !watching}
      menuFocused={menuFocused}
      onDismiss={() => setMenuFocused(false)}
      onSignOut={onExit}
      refreshing={refreshing}
      onRefresh={() => {
        /*
          Rilegge **saltando** l'archivio: è il senso del tasto.

          `refresh` a `true` fa ignorare la scadenza e rimanda comunque l'`ETag`, quindi se niente è
          cambiato costa un `304` e non un download. Chi lo preme dopo aver toccato il pannello
          riceve il catalogo nuovo; chi lo preme per abitudine non paga niente.
        */
        setRefreshing(true);
        void Promise.all([
          cache.channels(credentials, true),
          cache.categories(credentials, true),
          /*
            **Anche film e serie**, che prima questo tasto non toccava.

            Era il rovescio esatto di quel che serve: gli elenchi del VOD si riscaricavano da soli
            a scadenza — in un momento che nessuno aveva chiesto — e il gesto con cui qualcuno
            *chiede* di ricontrollare li lasciava com'erano. Adesso non scadono più da soli, quindi
            questo è l'unico posto da cui si aggiornano: dimenticarli qui vorrebbe dire un catalogo
            fermo per sempre.
          */
          cache.vod(credentials, 'movie', true),
          cache.vod(credentials, 'series', true),
          cache.vodCategoryList(credentials, 'movie', true),
          cache.vodCategoryList(credentials, 'series', true),
        ])
          .then((esiti) => {
            setCategoryNames(esiti[1]);
            // I programmi già scaricati restano: il tasto aggiorna il **catalogo**, e buttare l'EPG
            // vorrebbe dire riscaricare centinaia di finestre per un canale aggiunto.
            onCatalogRefreshed();
          })
          .finally(() => setRefreshing(false));
      }}
      profileName={profileName}
      performance={{ animations: settings.animations, visualEffects: settings.visualEffects }}
      onChoose={(id) => {
        setMenuFocused(false);
        if (!isSection(id)) return;
        // Dal menu si entra sempre dalla porta principale: quel che aveva chiesto la ricerca vale
        // per quel passaggio solo.
        setRequestedVodId(null);
        setRequestedCategoryId(null);
        // «Guida TV» dal menu è una richiesta di **sfogliare**. Le altre voci non aprono questa
        // schermata, e il valore che lasciano qui non lo legge nessuno.
        setArriveBrowsing(id === 'live');
        setSection(id);
      }}
    >
      {section === 'movies' || section === 'series' ? (
        /*
          Il catalogo on demand si monta **solo entrando**, e si smonta uscendo.

          Sono 6824 film e 2396 serie su un'installazione vera: chi guarda soltanto la televisione non
          deve pagarne la lettura. Lo smontaggio butta via l'elenco tenuto in memoria, non l'archivio
          sull'apparecchio — rientrando si ridisegna senza chiedere niente al gateway.
        */
        <VodHost
          composition={composition}
          cache={cache}
          credentials={credentials}
          kind={section === 'movies' ? 'movie' : 'series'}
          // Sul telefono Film e Serie sono due etichette in cima alla vetrina, non due voci di menu:
          // la sezione la tiene questo componente, quindi il cambio arriva qui.
          onPickKind={(quale) => setSection(quale === 'movie' ? 'movies' : 'series')}
          serverNowMs={serverNowMs}
          onRequestMenu={() => setMenuFocused(true)}
          menuHasFocus={menuFocused}
          onWatchingChange={setWatching}
          initialItemId={requestedVodId}
          // Il PIN si legge una volta sola, qui: tre letture dall'archivio per lo stesso dato
          // divergono alla prima modifica fatta da una delle tre.
          storedPin={storedPin}
        />
      ) : section === 'settings' ? (
        /*
          Le impostazioni esistono in due disegni, e la scelta è la **larghezza**.

          Non è la stessa schermata resa elastica: quella del televisore apre ogni voce a più valori
          in un elenco da percorrere col telecomando, e su un telefono quella forma costa un
          passaggio in più per cambiare un valore fra tre. Qui si tocca la riga e si passa al
          successivo — e **quale sia il successivo** lo dice `nextSettingValue`, nel dominio.

          Il salvataggio invece è uno solo, condiviso: due copie della stessa scrittura
          divergerebbero alla prima modifica fatta da una delle due, e il disallineamento si
          vedrebbe solo su uno dei due apparecchi.
        */
        phone ? (
          <PhoneSettingsScreen
            settings={settings}
            onToggle={(key) => applySettings(changeSetting(settings, key, !settings[key]))}
            onCycle={(key) => {
              const quale = CYCLE_KEY[key];
              applySettings(
                changeSetting(settings, quale, nextSettingValue(settings, quale) as never),
              );
            }}
            onOpenPin={() => setSection('pin')}
          />
        ) : (
        <SettingsScreen
          focusPort={composition.focusPort}
          settings={settings}
          onChange={applySettings}
          /*
            Il **nome** dell'installazione, non l'indirizzo: «STAGE» dice qualcosa a chi guarda,
            `https://stream.opik.net` no.

            Si ripiega sull'host quando il nome non c'è — istanza più vecchia che non lo manda, o
            credenziali archiviate prima di questa versione. Non è un ripiego silenzioso: è
            esattamente quel che si vedeva prima, ed è anche il dato che l'assistenza chiede per
            primo, quindi mostrarlo non toglie niente a nessuno.
          */
          host={(credentials.kind === 'token' ? credentials.serviceName : null) ?? credentials.host}
          subscription={subscription}
          /*
            La versione dell'artefatto installato, o un testo quando non c'è.

            `null` arriva dove l'app **non** è impacchettata — il server di sviluppo e le prove — e lì
            non c'è nessun artefatto da aggiornare. Si scrive «sconosciuta» e non «0.0.0»: un numero
            di comodo farebbe sembrare nuova ogni versione del registro, cioè proporrebbe un
            aggiornamento a chi è già all'ultima.
          */
          appVersion={appVersion() ?? translator.t('settings.appVersionUnknown')}
          updateVersion={update}
          favoritesCount={favorites.length}
          onOpenFavorites={() => setSection('favorites')}
          /*
            «Attivo» significa che **quattro cifre sono archiviate**, non che una levetta è alzata.
            Era un booleano nelle impostazioni senza nessuna schermata che lo riempisse, cioè un
            controllo genitori che prometteva una protezione inesistente.
          */
          pinSet={storedPin !== null}
          onOpenPin={() => setSection('pin')}
          // Indietro dalle impostazioni torna alla diretta, non all'accesso: è la schermata da cui si
          // è arrivati, e riportare all'accesso significherebbe ridigitare le credenziali.
          onExit={() => setSection('live')}
          onSignOut={onExit}
        />
        )
      ) : section === 'pin' ? (
        <PinScreen
          focusPort={composition.focusPort}
          pinSet={storedPin !== null}
          storedPin={storedPin}
          onSave={(pin) => {
            setStoredPin(pin);
            void cache.saveAdultPin(pin);
            /*
              Si torna alle impostazioni, non si resta qui.

              Un percorso che si chiude e lascia a schermo la stessa schermata fa dubitare che sia
              successo qualcosa, e il gesto più probabile dopo è rifarlo.
            */
            setSection('settings');
          }}
          onExit={() => setSection('settings')}
        />
      ) : section === 'home' && openAggregator !== null ? (
        <AggregatorHost
          composition={composition}
          cache={cache}
          credentials={credentials}
          aggregatorId={openAggregator.id}
          title={openAggregator.label}
          color={openAggregator.color}
          channels={channels}
          onOpenChannel={(channelId) => {
            setOpenAggregator(null);
            setRequestedChannelId(channelId);
            setArriveBrowsing(false);
            setSection('live');
          }}
          onOpenVod={(ref) => {
            setOpenAggregator(null);
            setRequestedVodId(ref);
            // Non si sa se è un film o una serie finché non lo si cerca in catalogo: `VodHost` apre
            // la scheda per identificativo, e il tipo lo decide lui. Si entra da Film, che è la
            // sezione che contiene entrambi i cataloghi.
            setSection('movies');
          }}
          onExit={() => setOpenAggregator(null)}
        />
      ) : section === 'home' ? (
        <HomeHost
          composition={composition}
          cache={cache}
          credentials={credentials}
          channels={channels}
          programsByChannel={loaded}
          categoryNames={categoryNames}
          onOpenChannel={(channelId) => {
            setRequestedChannelId(channelId);
            setArriveBrowsing(false);
            setSection('live');
          }}
          onOpenVod={(item) => {
            setRequestedVodId(item.id);
            setSection(item.kind === 'series' ? 'series' : 'movies');
          }}
          onOpenCategory={(categoryId) => {
            setRequestedCategoryId(categoryId);
            // Una piastrella di categoria dice «mostrami cosa c'è dentro». Prima portava alla
            // guida e faceva partire un canale sopra: la categoria c'era, ma sotto al video.
            setArriveBrowsing(true);
            setSection('live');
          }}
          onOpenAggregator={(id, label, color) => setOpenAggregator({ id, label, color })}
          onToggleReminder={onToggleReminder}
          reminderIds={reminderIds}
          animations={settings.animations}
          onExit={() => setSection('live')}
        />
      ) : section === 'search' ? (
        <SearchHost
          composition={composition}
          cache={cache}
          credentials={credentials}
          channels={channels}
          /*
            I programmi sono **quelli già scaricati** dalla guida, non una finestra chiesta apposta.

            Cercare vuol dire scrivere lettera per lettera: una chiamata per tasto premuto su un
            gateway che blocca a 3000 richieste ogni quarto d'ora è il modo più rapido di farsi
            bandire l'indirizzo. La schermata lo dichiara sotto il titolo della sezione, invece di
            far credere che quello sia tutto il palinsesto.
          */
          programs={searchablePrograms}
          nowMs={Date.now()}
          reminderIds={reminders}
          onToggleReminder={onToggleReminder}
          onOpenChannel={(channelId) => {
            setRequestedChannelId(channelId);
            setArriveBrowsing(false);
            setSection('live');
          }}
          onOpenVod={(item) => {
            // La scheda, non la vetrina: chi ha cercato un titolo e ha premuto OK vuole quel titolo.
            setRequestedVodId(item.id);
            setSection(item.kind === 'series' ? 'series' : 'movies');
          }}
          onExit={() => setSection('live')}
        />
      ) : section === 'reminders' ? (
        /*
          Quali promemoria compaiono lo decide `upcomingReminders`, nel dominio: solo quelli non
          ancora **finiti**, in ordine di inizio. Rifarlo qui sarebbe una seconda definizione di
          «ancora da venire», e le due divergerebbero sul programma in onda adesso.

          I programmi che gli si passano servono **solo per il titolo**: l'elenco si compone dalle
          chiavi dei promemoria, che portano canale e ora. Prima partiva dai programmi caricati, e su
          questa pagina non compariva niente di quel che si era segnato dalla home — e sul televisore
          non compariva **niente**, perché `phonePrograms` si riempie solo mentre la guida del
          telefono è a schermo.

          Due schermate e non una con le misure a parametro: una si tocca, l'altra si percorre col
          telecomando, e il titolo da 28 pixel della versione da telefono su 1920 non si legge.
        */
        phone ? (
          <PhoneRemindersScreen
            entries={remindersShown}
            channelNameOf={channelNameOf}
            nowMs={serverNowMs}
            onWatch={watchChannel}
            onRemove={onToggleReminder}
          />
        ) : (
          <RemindersScreen
            focusPort={composition.focusPort}
            entries={remindersShown}
            channelNameOf={channelNameOf}
            nowMs={serverNowMs}
            onWatch={watchChannel}
            onRemove={onToggleReminder}
            onExit={() => setSection('live')}
          />
        )
      ) : section === 'favorites' ? (
        <FavoritesScreen
          focusPort={composition.focusPort}
          channels={channels}
          favorites={favorites}
          /*
            Qui si **toglie** soltanto: aggiungere si fa dove il canale è già davanti agli occhi —
            la stella sulla riga della guida, o il lettore mentre lo si guarda. Una pagina in cui
            scegliere fra dodicimila caselle non si può usare proprio quando servirebbe.
          */
          onRemove={onToggleFavorite}
          onOpen={(channelId) => {
            // Si apre il canale: in un elenco di soli preferiti è l'azione ovvia su una casella.
            setRequestedChannelId(channelId);
            setArriveBrowsing(false);
            setSection('live');
          }}
          onExit={() => setSection('live')}
        />
      ) : phone && !phonePlaying ? (
        /*
          La guida del telefono: un elenco che si apre, non una griglia.

          Si monta **solo mentre non si sta guardando**. Il lettore è ancora quello della diretta, che
          a schermo pieno va bene anche qui — il video riempie lo schermo e il telecomando non serve.
          Sostituirlo adesso vorrebbe dire rifare anche la sintonia e il catch-up, cioè la parte che
          oggi funziona.
        */
        <PhoneGuideScreen
          /*
            Le categorie e il filtro vengono dalle **stesse funzioni** della guida grande.

            Rifarli qui sarebbe un secondo elenco e un secondo filtro: diverrebbero diversi al primo
            canale senza categoria, e nessuno dei due sarebbe riconoscibile come sbagliato guardandolo
            da solo — si vedrebbe solo che la TV e il telefono contano canali diversi.
          */
          categories={phoneCategories}
          activeCategoryId={phoneCategoryId}
          onPickCategory={(id) => {
            setPhoneCategoryId(id);
            // Cambiando filtro il canale aperto può non essere più in elenco: lasciarlo aperto
            // mostrerebbe un palinsesto che non appartiene a nessuna riga visibile.
            setOpenChannelId(null);
          }}
          channels={phoneChannels}
          /*
            I programmi del canale aperto, e solo quelli.

            `loaded` è già una mappa canale → programmi. Passarli tutti vorrebbe dire scorrere
            dodicimila canali per disegnarne uno: la schermata filtra per canale, ma il filtro
            costerebbe quanto l'elenco intero.
          */
          programs={phonePrograms}
          favorites={favorites}
          reminders={reminders}
          nowMs={serverNowMs}
          openChannelId={openChannelId}
          onToggleOpen={(channelId) => {
            const apre = channelId !== openChannelId;
            setOpenChannelId(apre ? channelId : null);
            // Si chiedono **aprendo**: chiederli per tutti all'ingresso sarebbe la lettura
            // dell'intero palinsesto per vedere una riga.
            if (apre) onNeedPrograms([channelId]);
          }}
          onToggleFavorite={onToggleFavorite}
          onToggleReminder={onToggleReminder}
          onWatch={(channelId) => {
            // Si passa al lettore della diretta, che è a schermo pieno: `watching` fa il resto.
            setRequestedChannelId(channelId);
            setArriveBrowsing(false);
            setPhonePlaying(true);
          }}
        />
      ) : (
        <LiveScreen
          focusPort={composition.focusPort}
          channels={channels}
          streamUrlFor={streamUrlFor}
          catchupUrlFor={catchupUrlFor}
          createPlayer={composition.createPlayer}
          programs={programs}
          guideWindow={guideWindow}
          serverNowMs={serverNowMs}
          favoriteIds={favoriteIds}
          reminderIds={reminderIds}
          categoryNames={categoryNames}
          onToggleFavorite={onToggleFavorite}
          onToggleReminder={onToggleReminder}
          onOpenFavorites={() => setSection('favorites')}
          onNeedPrograms={onNeedPrograms}
          onRequestMenu={() => {
            /*
              Indietro: sul telefono riporta alla guida, sul televisore apre la colonna.

              Sono lo stesso gesto con due destinazioni giuste diverse: col telecomando Indietro
              chiama il menu, col dito riporta da dove si è arrivati. Lasciare il solo
              `setMenuFocused` lascerebbe un telefono dentro il lettore **senza via d'uscita**.
            */
            if (phone) setPhonePlaying(false);
            else setMenuFocused(true);
          }}
          menuHasFocus={menuFocused}
          touch={phone}
          fullscreen={phoneFullscreen}
          /*
            Il tasto compare **solo sul telefono**: sul televisore si è già a tutto schermo, e un
            tasto che non fa niente è peggio di un tasto che manca.

            In orizzontale premerlo spegne la richiesta manuale, che però lì non comanda: si esce
            dallo schermo intero rigirando il telefono. Il tasto in quel verso serve a tornare
            all'interfaccia senza rigirarlo, e per questo scrive `false` comunque.
          */
          onToggleFullscreen={phone ? () => setFullscreenAsked((prima) => !prima) : undefined}
          initialChannelId={requestedChannelId ?? lastChannelId}
          openGuide={arriveBrowsing}
          initialCategoryId={requestedCategoryId}
          onChannelTuned={(channelId) => void cache.rememberChannel(channelId)}
          onWatchingChange={setWatching}
          onSendBackToLogin={onSendBackToLogin}
        />
      )}
    </AppChrome>
  );
}

/**
 * Le voci del menu, nell'ordine di 03-SCHERMATE-TV §2.
 *
 * Film e Serie TV stanno **al loro posto**, subito dopo la guida, e non in coda: l'ordine è parte del
 * disegno, e un menu che cresce per accodamento diventa l'elenco cronologico di chi l'ha scritto.
 * Mancano ancora `Home` e `Cerca`.
 *
 * `Esci` non è qui: nel prototipo è un tasto staccato in fondo alla colonna, sotto il profilo, e lo
 * disegna il guscio. Metterlo fra le voci mescolava «vai in una sezione» con «disconnetti questo
 * apparecchio».
 */
const MENU_ENTRIES: ReadonlyArray<MenuEntry> = [
  { id: 'home', labelKey: 'menu.home' },
  { id: 'live', labelKey: 'menu.guide' },
  { id: 'search', labelKey: 'menu.search' },
  { id: 'movies', labelKey: 'menu.movies' },
  { id: 'series', labelKey: 'menu.series' },
  { id: 'favorites', labelKey: 'menu.favorites' },
  { id: 'reminders', labelKey: 'menu.reminders' },
  { id: 'settings', labelKey: 'menu.settings' },
];

/** Le sezioni raggiungibili dal menu. Un valore solo: due booleani per cinque stati non si tengono. */
/*
  `pin` non è nel menu, ed è voluto: ci si arriva **dalle impostazioni**, che è dove qualcuno va a
  cercare il controllo genitori. Una voce di menu in più sarebbe una riga che quasi nessuno apre, e
  che chi la apre trova già dove si aspetta.

  `isSection` la esclude di conseguenza: quel che arriva dal menu non può valere `pin`.
*/
const SECTIONS = ['home', 'live', 'search', 'movies', 'series', 'favorites', 'reminders', 'settings'] as const;
type Section = (typeof SECTIONS)[number] | 'pin';

function isSection(value: string): value is Section {
  return (SECTIONS as ReadonlyArray<string>).includes(value);
}
